/**
 * Everything that is set rather than watched, placed where the screen has room.
 *
 * On a wide screen it all sits inline, as it always has. On a phone the meters
 * have to fit the screen without scrolling, so the same controls move into a
 * bottom sheet that opens on demand — thumb-high, and out of the way while
 * tuning. Nothing here is touched mid-strum.
 *
 * Exactly one copy is rendered at a time: two would put two controls with the
 * same label into the document, which is confusing for a screen reader and
 * ambiguous for anything looking a control up by name.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { SettingsPanel } from './SettingsPanel'
import { DeviceSelect } from '../DeviceSelect'
import { LayoutToggle } from '../tuner/LayoutToggle'
import { COMPACT_QUERY, useMediaQuery } from '../useMediaQuery'

interface SettingsProps {
  /** Switch the audio input. */
  readonly onSelectDevice: (deviceId: string) => void
  /** Re-read the input list, e.g. when the picker is opened. */
  readonly onOpenDevices: () => void
}

export function Settings({ onSelectDevice, onOpenDevices }: SettingsProps): React.JSX.Element {
  const isCompact = useMediaQuery(COMPACT_QUERY)
  const [isOpen, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const sheetRef = useRef<HTMLDivElement | null>(null)

  const close = useCallback(() => {
    setOpen(false)
    triggerRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!isOpen) return undefined
    sheetRef.current?.focus()

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [close, isOpen])

  const controls = (
    <>
      <SettingsPanel />
      <div className="controls">
        <DeviceSelect onSelect={onSelectDevice} onOpen={onOpenDevices} />
        <div className="controls__field controls__field--wide">
          <span>Ansicht</span>
          <LayoutToggle />
        </div>
      </div>
    </>
  )

  // Widening the window puts everything back inline and takes the sheet with
  // it; nothing has to reset, because the sheet simply stops existing.
  if (!isCompact) return controls

  return (
    <div className="settings-sheet">
      <button
        type="button"
        className="button settings-sheet__trigger"
        ref={triggerRef}
        aria-expanded={isOpen}
        onClick={() => {
          setOpen(true)
        }}
      >
        Einstellungen
      </button>

      {isOpen && (
        <>
          <div
            className="settings-sheet__backdrop"
            onClick={close}
            // Decorative: the sheet itself carries the accessible way out.
            aria-hidden="true"
          />
          <div
            className="settings-sheet__panel"
            role="dialog"
            aria-modal="true"
            aria-label="Einstellungen"
            tabIndex={-1}
            ref={sheetRef}
          >
            <div className="settings-sheet__head">
              <span className="settings-sheet__title">Einstellungen</span>
              <button type="button" className="button settings-sheet__close" onClick={close}>
                Fertig
              </button>
            </div>
            {controls}
          </div>
        </>
      )}
    </div>
  )
}
